import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { FlexLayout } from 'layouts'
import WebApp from '@twa-dev/sdk'
import cn from 'classnames'

import { ChartStore } from 'stores'
import { useValueContext, ValueContext } from 'contexts'
import { BaseModal, Button, Link, PageSpinner } from 'components'
import { isDef, useApp } from 'utils'
import { botUrl } from 'consts'

import { EventsChart } from './components'
import './styles.scss'

interface ModalData {
  open: boolean
  title: ReactNode
  children: ReactNode
}

const initialModalData: ModalData = { open: false, title: '', children: null }

export const Chart = observer(() => {
  const { subscription } = useApp()
  const { selectedBaby: baby } = useValueContext()
  const navigate = useNavigate()
  const [store] = useState(() => new ChartStore())
  const [height, setHeight] = useState(0)
  const [modalData, setModalData] = useState(initialModalData)
  const ref = useRef<HTMLDivElement>(null)
  const { state } = useLocation()

  const { daysToShow, loadEvents, loadMoreEvents, setBaby, events, loading, loadingMore } = store

  const isPremium = subscription?.hasActiveSubscription

  const onFeedbackClick = (e: React.MouseEvent) => {
    e.preventDefault()
    WebApp.openTelegramLink(botUrl)
  }

  useEffect(() => {
    if (!baby) {
      return
    }

    setBaby(baby)

    if (isDef(isPremium)) {
      loadEvents(isPremium)
    }
  }, [baby, isPremium, loadEvents, setBaby])

  useEffect(() => {
    const successfulPaymentText =
      'Теперь вам доступны графики и статистика за все время. Спасибо, что доверяете нам 🤗'
    const failedPaymentJsx = (
      <>
        Попробуйте еще раз или
        <br />
        <Link onClick={onFeedbackClick}>напишите в поддержку</Link>
      </>
    )
    const handlingPaymentJsx = (
      <>
        <div>Пожалуйста, подождите немного и обновите страницу</div>
        <Button className="chart-reload-button" onClick={() => window.location.reload()}>
          Обновить
        </Button>
      </>
    )

    if (state?.paymentSuccess) {
      if (subscription && isPremium) {
        setModalData({
          open: true,
          title: '🥳 Подписка активирована',
          children: successfulPaymentText,
        })
      } else if (subscription && !isPremium) {
        setModalData({
          open: true,
          title: '🤔 Платеж в обработке',
          children: handlingPaymentJsx,
        })
      }
    } else if (isDef(state?.paymentSuccess) && !state?.paymentSuccess) {
      setModalData({
        open: true,
        title: '😞 Ошибка оплаты',
        children: failedPaymentJsx,
      })
    }
  }, [isPremium, state?.paymentSuccess, subscription])

  useLayoutEffect(() => {
    if (ref.current && subscription) {
      let height = ref.current.clientHeight

      if (!isPremium) {
        height -= 100
      }

      setHeight(height)
    }
  }, [isPremium, subscription])

  return (
    <ValueContext.Provider value={store}>
      {subscription ? (
        <>
          <PageSpinner delay={400} minVisibleTime={400} open={loading} />
          <BaseModal
            className="chart-modal"
            {...modalData}
            onClose={() => setModalData(initialModalData)}
          />
          <FlexLayout.Container
            className={cn('chart-container with-mobile-indent with-menu-padding', {
              premium: isPremium,
            })}
            vertical
          >
            <FlexLayout.Content ref={ref} className="scrollable-layout" scrollable={!isPremium}>
              <EventsChart
                events={events}
                daysToShow={daysToShow}
                premium={isPremium}
                loading={loadingMore}
                onLoadMore={loadMoreEvents}
                parentHeight={height}
              />
              {!isPremium && (
                <Button className="buy-premium-btn" onClick={() => navigate('/premium')}>
                  Купить
                  <span>🌟 Премиум подписку 🌟</span>
                </Button>
              )}
            </FlexLayout.Content>
          </FlexLayout.Container>
        </>
      ) : (
        <PageSpinner minVisibleTime={400} open={!subscription} opaque />
      )}
    </ValueContext.Provider>
  )
})
